/**
 * FeatureTour — Tour interattivo di 60 secondi che mostra le feature premium
 *
 * Overlay step-by-step che evidenzia elementi dell'interfaccia e spiega
 * cosa si sblocca con Rider Plus.
 *
 * Da lanciare: al primo login dopo la registrazione, o dal menu "Scopri Premium".
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Shield, Map, Users, Zap, ChevronRight, X, Star } from 'lucide-react';

interface TourStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  feature: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    icon: <Shield size={28} className="text-green-400" />,
    title: 'RideGuard',
    description: 'Tracciamento live della tua corsa. I tuoi amici e familiari possono seguirti in tempo reale sulla mappa.',
    feature: 'rideguard',
  },
  {
    icon: <Map size={28} className="text-blue-400" />,
    title: 'Mappa Interattiva Premium',
    description: 'Visualizza tutti i biker attivi, unisciti ai gruppi, scopri nuovi percorsi nella tua zona.',
    feature: 'livemap',
  },
  {
    icon: <Users size={28} className="text-purple-400" />,
    title: 'Chapter Illimitati',
    description: 'Crea e gestisci i tuoi chapter. Organizza eventi, raduni e tieni traccia dei membri.',
    feature: 'chapters',
  },
  {
    icon: <Zap size={28} className="text-amber-400" />,
    title: 'Priorità Matching',
    description: 'Vieni abbinato per primo ai gruppi nella tua zona. Meno attesa, più guida.',
    feature: 'priority',
  },
];

interface FeatureTourProps {
  /** Se true, mostra il tour */
  open: boolean;
  /** Callback alla chiusura */
  onClose: () => void;
}

export default function FeatureTour({ open, onClose }: FeatureTourProps) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const handleFinish = useCallback(() => {
    localStorage.setItem('rc-tour-done', '1');
    onClose();
  }, [onClose]);

  const handleUpgrade = useCallback(() => {
    onClose();
    navigate('/subscribe');
  }, [navigate, onClose]);

  if (!open) return null;

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9998]" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-gray-950/95 backdrop-blur-2xl border border-white/10 rounded-3xl max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-scaleIn">
          {/* Progress bar */}
          <div className="h-1 bg-gray-800">
            <div
              className="h-full bg-gradient-to-r from-brand-500 to-purple-500 transition-all duration-500"
              style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }}
            />
          </div>

          <div className="p-6 text-center">
            {/* Icon */}
            <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              {current.icon}
            </div>

            {/* Title */}
            <h2 className="text-xl font-extrabold text-white mb-2">{current.title}</h2>
            <p className="text-neutral-400 text-sm mb-4 leading-relaxed">{current.description}</p>

            {/* Feature badge */}
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-bold">
              <Crown size={12} />
              Esclusiva Rider Plus
            </span>
          </div>

          {/* Footer */}
          <div className="p-4 pt-0 flex flex-col gap-2">
            <div className="flex justify-center gap-2 mb-2">
              {TOUR_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`rounded-full transition-all ${
                    i === step
                      ? 'bg-brand-500 w-6 h-2'
                      : 'bg-gray-700 w-2 h-2 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="px-4 py-3 rounded-2xl bg-gray-800 text-white font-bold text-sm hover:bg-gray-700"
                >
                  ← Indietro
                </button>
              )}
              {isLast ? (
                <button
                  onClick={handleUpgrade}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-brand-500 to-orange-600 text-white font-bold text-sm shadow-[0_4px_16px_rgba(255,90,31,0.4)]"
                >
                  <Crown size={18} />
                  Attiva Rider Plus
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={() => setStep(s => s + 1)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-500 text-white font-bold text-sm"
                >
                  Avanti
                  <ChevronRight size={16} />
                </button>
              )}
            </div>

            <button
              onClick={handleFinish}
              className="w-full py-2 text-neutral-500 hover:text-neutral-300 text-xs"
            >
              {isLast ? 'No grazie, continuo con Free' : 'Salta tour'}
            </button>
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-gray-800 text-neutral-400 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
}
